import { Package, Save, X, Layers } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Product, TaxRate, ProductNature } from '@/types/models';
import { productsAPI } from '@/utils/productsAPI';
import type { ProductCreate } from '@/utils/productsAPI';
import { useProductCategories } from '@/features/catalog/hooks/useProducts';
import { CompositeEditor } from './CompositeEditor';
import { PriceTierEditor } from './PriceTierEditor';
import { ClientPricingEditor } from './ClientPricingEditor';

const PRODUCT_NATURE_VALUES: ProductNature[] = [
  'FOURNITURE', 'MAIN_OEUVRE', 'SOUS_TRAITANCE', 'MATERIEL', 'SERVICE', 'DIVERS',
];

interface ProductFormProps {
  product: Product | null;
  tvaRates: TaxRate[];
  onSubmit: (product: ProductCreate) => void;
  onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  tvaRates,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ProductCreate>({
    name: '',
    description: '',
    technicalDescription: '',
    sku: '',
    unitPrice: 0,
    taxRateId: '',
    unit: 'unité',
    categoryId: undefined,
    stockQuantity: null,
    lowStockAlert: null,
    // S7 — Nature et ouvrage composé
    productType: 'SERVICE',
    costPrice: null,
    marginPercent: null,
    isComposite: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [manageStock, setManageStock] = useState(false);
  const [isComposite, setIsComposite] = useState(false);
  const { data: categories = [], isLoading: loadingCategories } = useProductCategories();

  // Charger les données du produit en édition
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description || '',
        technicalDescription: product.technicalDescription || '',
        sku: product.sku || '',
        unitPrice: Number(product.unitPrice) || 0,
        taxRateId: product.taxRateId || '',
        unit: product.unit || 'unité',
        categoryId: product.categoryId || undefined,
        stockQuantity: product.stockQuantity || null,
        lowStockAlert: product.lowStockAlert || null,
        // S7
        productType: product.productType || 'SERVICE',
        costPrice: product.costPrice != null ? Number(product.costPrice) : null,
        marginPercent: product.marginPercent != null ? Number(product.marginPercent) : null,
        isComposite: product.isComposite || false,
      });
      setManageStock(!!product.trackInventory);
      setIsComposite(!!product.isComposite);
    }
  }, [product]);

  const handleChange = (field: keyof ProductCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }

    if (formData.unitPrice !== undefined && formData.unitPrice < 0) {
      newErrors.unitPrice = t('validation.positiveNumber');
    }

    if (!formData.taxRateId) {
      newErrors.taxRateId = t('products.validation.taxRateRequired');
    }

    if (manageStock) {
      if (formData.stockQuantity !== null && formData.stockQuantity !== undefined && formData.stockQuantity < 0) {
        newErrors.stockQuantity = t('products.validation.stockNonNegative');
      }
      if (formData.lowStockAlert !== null && formData.lowStockAlert !== undefined && formData.lowStockAlert < 0) {
        newErrors.lowStockAlert = t('products.validation.thresholdNonNegative');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const dataToSubmit = {
      ...formData,
      unitPrice: typeof formData.unitPrice === 'string' ? parseFloat(formData.unitPrice) : formData.unitPrice,
      trackInventory: manageStock,
      stockQuantity: manageStock ? formData.stockQuantity : null,
      lowStockAlert: manageStock ? formData.lowStockAlert : null,
      isComposite,
      costPrice: isComposite ? null : formData.costPrice,
      marginPercent: isComposite ? null : formData.marginPercent,
    };

    onSubmit(dataToSubmit);
  };

  // Calculer le prix TTC
  const getCurrentTaxRate = () => {
    if (!formData.taxRateId) return 0;  // ✅ CORRIGÉ (était defaultTaxRateId)
    const taxRate = tvaRates.find(rate => rate.id === formData.taxRateId);  // ✅ CORRIGÉ
    return taxRate ? taxRate.rate : 0;
  };
  const priceTTC = (formData.unitPrice || 0) * (1 + getCurrentTaxRate() / 100);  // ✅ CORRIGÉ (était baseUnitPrice)

  // ❌ SUPPRIMÉ: Liste hardcodée de catégories - maintenant chargées depuis l'API

  // Unités prédéfinies
  const units = [
    'unité',
    'heure',
    'jour',
    'mois',
    'année',
    'm²',
    'm³',
    'kg',
    'L',
    'forfait',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-400/20 w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden animate-fade-in-up border border-white/50">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/30 bg-gradient-to-r from-teal-50/80 to-white/60 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Package className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {product ? t('products.edit') : t('products.create')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('products.formDescription')}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          {/* Informations de base */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Nom */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('products.fields.name')} *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                  errors.name ? "border-red-500" : "border-gray-300"
                }`}
                placeholder={t('products.placeholders.name')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('products.fields.description')}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors resize-none"
                placeholder={t('products.placeholders.description')}
              />
            </div>

            {/* Description technique */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('products.fields.technicalDescription', 'Description technique (normes, DTU, spécifications)')}
              </label>
              <textarea
                value={formData.technicalDescription || ''}
                onChange={(e) => handleChange("technicalDescription", e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors resize-none"
                placeholder={t('products.placeholders.technicalDescription', 'Ex: Pose collée C2S1 sur chape, joints 3mm, NF DTU 52.2...')}
              />
            </div>

            {/* Référence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('products.fields.sku')}
              </label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => handleChange("sku", e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                placeholder={t('products.placeholders.referenceExample')}
              />
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('products.fields.category')}
              </label>
              <select
                value={formData.categoryId || ''}
                onChange={(e) => handleChange("categoryId", e.target.value || undefined)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories ? t('common.loading') : t('products.placeholders.selectCategory')}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* S7 — Nature comptable et ouvrage composé */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              {t('products.sections.natureAndType')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nature comptable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('products.nature.label')}
                </label>
                <select
                  value={formData.productType || 'SERVICE'}
                  onChange={(e) => handleChange('productType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                >
                  {PRODUCT_NATURE_VALUES.map((value) => (
                    <option key={value} value={value}>
                      {t(`products.nature.${value}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Toggle ouvrage composé */}
              <div className="flex items-end">
                <label className="flex items-center cursor-pointer gap-3 px-4 py-3 border border-gray-300 rounded-xl w-full hover:border-teal-500 transition-colors">
                  <input
                    type="checkbox"
                    checked={isComposite}
                    onChange={(e) => {
                      setIsComposite(e.target.checked);
                      handleChange('isComposite', e.target.checked);
                    }}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <Layers className="w-4 h-4 text-teal-600" />
                  <span className="text-sm text-gray-700 font-medium">
                    {t('products.composite.toggle')}
                  </span>
                </label>
              </div>

              {/* Prix de revient / Marge (seulement pour produits simples) */}
              {!isComposite && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('products.composite.costPrice')}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.costPrice ?? ''}
                        onChange={(e) => handleChange('costPrice', e.target.value ? parseFloat(e.target.value) : null)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                        placeholder={t('common.labels.optional', { ns: 'common' })}
                      />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">€</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('products.composite.marginPercent')}
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1000"
                        value={formData.marginPercent ?? ''}
                        onChange={(e) => handleChange('marginPercent', e.target.value ? parseFloat(e.target.value) : null)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                        placeholder={t('common.labels.optional', { ns: 'common' })}
                      />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">%</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* S7 — Éditeur de composants (si ouvrage composé) */}
          {isComposite && product?.id && (
            <div className="border-t border-gray-200 pt-6">
              <CompositeEditor
                productId={product.id}
                onPriceUpdate={(sellingPrice) => {
                  setFormData(prev => ({ ...prev, unitPrice: sellingPrice }));
                }}
              />
            </div>
          )}

          {isComposite && !product?.id && (
            <div className="border-t border-gray-200 pt-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-700">
                  {t('products.composite.saveFirst')}
                </p>
              </div>
            </div>
          )}

          {/* Prix et TVA */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              {t('products.sections.priceAndTax')}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Prix unitaire HT */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('products.fields.priceHT')} {!isComposite && '*'}
                  {isComposite && <span className="text-xs text-gray-500 ml-1">({t('products.composite.calculated')})</span>}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unitPrice || 0}
                    onChange={(e) =>
                      handleChange(
                        "unitPrice",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    readOnly={isComposite}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                      errors.unitPrice ? "border-red-500" : "border-gray-300"
                    } ${isComposite ? "bg-gray-50 cursor-not-allowed" : ""}`}
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                    €
                  </span>
                </div>
                {errors.unitPrice && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.unitPrice}
                  </p>
                )}
              </div>

              {/* Unité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('products.fields.unit')}
                </label>
                <select
                  value={formData.unit || 'unité'}
                  onChange={(e) => handleChange("unit", e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors"
                >
                  {units.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
                  ))}
                </select>
              </div>

              {/* TVA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('products.fields.taxRate')} *
                </label>
                <select
                  value={formData.taxRateId || ''}
                  onChange={(e) =>
                    handleChange("taxRateId", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                    errors.taxRateId ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">{t('products.placeholders.selectTaxRate')}</option>
                  {tvaRates.map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.name} ({rate.rate}%)
                    </option>
                  ))}
                </select>
                {errors.taxRateId && (
                  <p className="mt-1 text-sm text-red-600">{errors.taxRateId}</p>
                )}
              </div>

              {/* Prix TTC (calculé) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('products.fields.priceTTC')}
                </label>
                <div className="w-full px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl font-semibold text-teal-700">
                  {priceTTC.toFixed(2)} €
                </div>
              </div>
            </div>
          </div>

          {/* S10 — Tarifs dégressifs */}
          {product?.id && (
            <div className="border-t border-gray-200 pt-6">
              <PriceTierEditor
                productId={product.id}
                baseUnitPrice={formData.unitPrice || 0}
                initialPricingMode={product.pricingMode}
              />
            </div>
          )}

          {!product?.id && (
            <div className="border-t border-gray-200 pt-6">
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs text-gray-500">
                  {t('products.tiers.saveFirstHint', 'Enregistrez le produit pour configurer les tarifs dégressifs.')}
                </p>
              </div>
            </div>
          )}

          {/* S13 — Tarifs par client */}
          {product?.id && (
            <div className="border-t border-gray-200 pt-6">
              <ClientPricingEditor
                productId={product.id}
                baseUnitPrice={formData.unitPrice || 0}
              />
            </div>
          )}

          {/* Gestion du stock */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t('products.sections.stockManagement')}
              </h3>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={manageStock}
                  onChange={(e) => setManageStock(e.target.checked)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  {t('products.fields.manageStock')}
                </span>
              </label>
            </div>

            {manageStock && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Quantité en stock */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('products.fields.stockQuantity')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.stockQuantity || ""}
                    onChange={(e) =>
                      handleChange(
                        "stockQuantity",
                        e.target.value ? parseFloat(e.target.value) : null
                      )
                    }
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                      errors.stockQuantity
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="0"
                  />
                  {errors.stockQuantity && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.stockQuantity}
                    </p>
                  )}
                </div>

                {/* Seuil d'alerte */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('products.fields.lowStockAlert')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.lowStockAlert || ""}
                    onChange={(e) =>
                      handleChange(
                        "lowStockAlert",
                        e.target.value ? parseFloat(e.target.value) : null
                      )
                    }
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
                      errors.lowStockAlert
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    placeholder="0"
                  />
                  {errors.lowStockAlert && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.lowStockAlert}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {t('products.lowStockAlertHelp')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white bg-teal-600 hover:bg-teal-700 hover:shadow-lg rounded-xl font-medium transition-all"
            >
              <Save className="w-4 h-4" />
              {product ? t('common.actions.update') : t('products.createProduct')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;




